import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { CustomEncoder, LocalStorageKeys, LocalStorageService } from '@iot-platform/core';
import { SortEntitiesUtil } from '@iot-platform/iot-platform-utils';
import { get, orderBy, uniqBy } from 'lodash';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { FilterFieldOptions } from '../models';

@Injectable({ providedIn: 'root' })
export class DynamicListFieldService {
  private readonly httpClient: HttpClient = inject(HttpClient);
  private readonly storage: LocalStorageService = inject(LocalStorageService);

  getQueryParams(options: Partial<FilterFieldOptions>): HttpParams {
    let params: HttpParams = new HttpParams({ encoder: new CustomEncoder() });
    if (get(options, ['mapBusinessProfileEntityId'])) {
      try {
        const bpEntityId = get(JSON.parse(this.storage.get(LocalStorageKeys.STORAGE_BUSINESS_PROFILE_KEY)), 'entityId');
        if (bpEntityId) {
          params = params.set('entityId', bpEntityId);
        }
      } catch (ex) {
        /* do nothing */
      }
    }
    return params;
  }

  getDynamicList(options: Partial<FilterFieldOptions>): Observable<any[]> {
    return this.httpClient
      .get(get(options, 'url') as string, { params: this.getQueryParams(options) })
      .pipe(map((response) => this.parseApiResponse(response, options)));
  }

  sortDynamicList(data: any[], options: Partial<FilterFieldOptions>): any[] {
    const sortMethod = get(options, 'sortMethod') as never;
    const arrayOrObject = get(options, ['arrayOrObject'], 'object');
    const type = get(sortMethod, ['type'], '') as string;
    switch (type) {
      case 'alphabetically':
      case 'asc':
      case 'desc': {
        if (arrayOrObject === 'object') {
          const sorted = orderBy(data, [get(sortMethod, ['property'], 'name')], [type === 'desc' ? 'desc' : 'asc']);
          const inputs: any[] = get(options, 'inputs', [])?.filter((input) => get(input, 'parentListProperty'));
          if (inputs.length) {
            return sorted.map((item) =>
              inputs.reduce((acc, input) => {
                const prop = get(input, 'parentListProperty') as string;
                const dir = get(input, ['sortMethod', 'type']) === 'desc' ? 'desc' : 'asc';
                const property = get(input, ['sortMethod', 'property'], 'name') as string;
                return Array.isArray(acc[prop]) ? { ...acc, [prop]: orderBy(acc[prop], [property], [dir]) } : acc;
              }, item)
            );
          }
          return sorted;
        }
        return data.sort();
      }
      case 'entityHierarchically':
        return SortEntitiesUtil.sortHierarchically(data);
      case '':
        return data.sort();
      default:
        return data;
    }
  }

  private parseApiResponse(response: any, options: Partial<FilterFieldOptions>): any[] {
    let values = [];

    if (Array.isArray(response)) {
      values = response;
    } else if (response.hasOwnProperty('content')) {
      values = response.content;
    }

    if (options.distinctValues && options?.selectByProperty) {
      values = uniqBy(values, options.selectByProperty);
    }

    // Filter items before apply transformers
    values = this.sortDynamicList(values, options);

    if (options.dataTransform) {
      values = options.dataTransform(values);
    } else if (values?.length) {
      const first: any = values[0];
      if (!(first instanceof Object)) {
        // Default transformation to key/value pairs for arrays of primitive types
        values = values.reduce((acc, value) => {
          acc.push({
            key: value,
            value
          });
          return acc;
        }, []);
      }
    }

    return values;
  }
}
